import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconCalendarClock } from "@tabler/icons-react";

import { Button, Modal } from "@fragments";

import { STEPS } from "./enums/steps";
import { getVisibilityOptions } from "./enums/visibilityOptions";
import { getNotifyOptions } from "./enums/notifyOptions";
import { localToDisplay } from "./helpers/schedule";
import usePublishPost from "./hooks/usePublishPost";
import PublishPostView from "./view";
import VisibilityPopper from "./view/components/Visibility";
import SchedulePopper from "./view/components/Schedule";
import NotifyPopper from "./view/components/Notify";

const POPPERS = {
    VISIBILITY: "visibility",
    SCHEDULE: "schedule",
    NOTIFY: "notify",
};

export default function PublishPostModal({ modalRef }) {
    const { t } = useTranslation("groupModals");
    const {
        open,
        step,
        selection,
        editing,
        picker,
        visibility,
        setVisibility,
        loading,
        configRef,
        exerciseRegistry,
        setExerciseRegistry,
        uploadBusy,
        handleUploadBusyChange,
        isManager,
        schedule,
        isScheduled,
        setScheduleNow,
        setScheduleOn,
        setScheduleValue,
        notifyAvailable,
        notifyMembers,
        setNotifyMembers,
        activePopper,
        setActivePopper,
        goToGroupStep,
        goToPostStep,
        handleSubmit,
        handleClose,
    } = usePublishPost({ modalRef });

    const visibilityAnchorRef = useRef(null);
    const scheduleAnchorRef = useRef(null);
    const notifyAnchorRef = useRef(null);

    const isGroupStep = step === STEPS.GROUP;
    const showNotify = isManager && notifyAvailable;

    // Footer option buttons read as the current choice: its icon + label.
    const visibilityOptions = useMemo(() => getVisibilityOptions(t), [t]);
    const currentVisibility =
        visibilityOptions.find((o) => o.value === visibility) || visibilityOptions[0];
    const VisibilityIcon = currentVisibility.Icon;

    const notifyOptions = useMemo(() => getNotifyOptions(t), [t]);
    const currentNotify = notifyOptions.find((o) => o.value === !!notifyMembers) || notifyOptions[0];
    const NotifyIcon = currentNotify.Icon;

    const scheduleLabel =
        isScheduled && schedule.value
            ? localToDisplay(schedule.value)
            : isScheduled
                ? t("publishPost.schedule.options.scheduled.label")
                : t("publishPost.schedule.options.now.label");

    // Both steps always show in the trail: the current one strong, the other
    // muted and clickable only when it can actually be reached. An edit has
    // no group step — the post already lives somewhere.
    const breadcrumb = editing
        ? undefined
        : [
              {
                  label: selection.group?.name || t("publishPost.steps.group"),
                  current: isGroupStep,
                  onClick: goToGroupStep,
              },
              {
                  label: t("publishPost.steps.post"),
                  current: !isGroupStep,
                  disabled: !selection.group,
                  onClick: goToPostStep,
              },
          ];

    let title = t("publishPost.title");
    if (editing) title = t("publishPost.editTitle");
    else if (isGroupStep) title = t("publishPost.selectGroup.title");

    let subtitle = selection.community?.name;
    if (editing) subtitle = selection.group?.name || selection.community?.name;
    else if (isGroupStep) subtitle = t("publishPost.selectGroup.subtitle");

    // "Publicar agora" already names the schedule choice beside it, so the
    // submit says what it does: publish, schedule, or save the scheduled edit.
    let submitLabel = t("publishPost.submitNow");
    if (isScheduled) submitLabel = editing ? t("publishPost.saveChanges") : t("publishPost.submitScheduled");

    function togglePopper(name) {
        setActivePopper((current) => (current === name ? null : name));
    }

    // A panel's click-away also fires when another footer button is pressed;
    // only close when this panel is still the open one, so switching works.
    function closePopper(name) {
        setActivePopper((current) => (current === name ? null : current));
    }

    // `title` names the setting for assistive tech (and for the icon-only
    // phone layout): "Quando publicar: 27 de setembro, às 09:00".
    function optionButton(name, anchorRef, icon, label, title) {
        return (
            <span ref={anchorRef} className="publish-post-footer__anchor">
                <Button
                    className="publish-post-footer__option"
                    variant="rounded"
                    color="overlay"
                    size="medium"
                    startIcon={icon}
                    onClick={() => togglePopper(name)}
                    aria-haspopup="dialog"
                    aria-expanded={activePopper === name}
                    aria-label={`${title}: ${label}`}
                    preventLoading
                >
                    {label}
                </Button>
            </span>
        );
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            // Wide enough for three option buttons + submit on one row.
            size="lg"
            className="publish-post-modal"
            breadcrumb={breadcrumb}
            title={title}
            subtitle={subtitle}
            // The group step advances on a pick and closes from the header, so
            // it needs no footer; the post step's footer holds its options + submit.
            footer={
                isGroupStep ? undefined : (
                    <div className="publish-post-footer">
                        <div className="publish-post-footer__options">
                            {optionButton(POPPERS.VISIBILITY, visibilityAnchorRef, <VisibilityIcon />, currentVisibility.label, t("publishPost.visibility.title"))}
                            {isManager &&
                                optionButton(POPPERS.SCHEDULE, scheduleAnchorRef, <IconCalendarClock />, scheduleLabel, t("publishPost.schedule.title"))}
                            {showNotify &&
                                optionButton(POPPERS.NOTIFY, notifyAnchorRef, <NotifyIcon />, currentNotify.button, t("publishPost.notify.title"))}
                        </div>

                        <Button
                            className="publish-post-footer__submit"
                            size="large"
                            onClick={handleSubmit}
                            loading={loading}
                            disabled={uploadBusy || !selection.group}
                        >
                            {submitLabel}
                        </Button>
                    </div>
                )
            }
        >
            <PublishPostView
                step={step}
                selection={selection}
                picker={picker}
                configRef={configRef}
                exerciseRegistry={exerciseRegistry}
                onExerciseRegistryChange={setExerciseRegistry}
                onUploadBusyChange={handleUploadBusyChange}
                editing={editing}
            />

            <VisibilityPopper
                open={open && activePopper === POPPERS.VISIBILITY}
                anchorEl={visibilityAnchorRef.current}
                value={visibility}
                onChange={setVisibility}
                onClose={() => closePopper(POPPERS.VISIBILITY)}
            />

            {isManager && (
                <SchedulePopper
                    open={open && activePopper === POPPERS.SCHEDULE}
                    anchorEl={scheduleAnchorRef.current}
                    onClose={() => closePopper(POPPERS.SCHEDULE)}
                    schedule={schedule}
                    onNow={setScheduleNow}
                    onSchedule={setScheduleOn}
                    onValueChange={setScheduleValue}
                />
            )}

            {showNotify && (
                <NotifyPopper
                    open={open && activePopper === POPPERS.NOTIFY}
                    anchorEl={notifyAnchorRef.current}
                    value={!!notifyMembers}
                    onChange={setNotifyMembers}
                    onClose={() => closePopper(POPPERS.NOTIFY)}
                />
            )}
        </Modal>
    );
}
